import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { OutputAvailability, OutputContractKind, StepOutputContract } from '@/types/automationFields.ts';
import { Badge, Box, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconBraces } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import {
  getContractColor,
  getContractKindLabel,
  getAvailabilityLabel,
  getSchemaLabel,
  getSourceLabel,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/dataInputSchemaContractUtils.ts';

type Props = {
  outputContracts: StepOutputContract[];
  bottomSpacing?: string | number;
  showSourceLabel?: boolean;
};

const getArtifactLabel = (contract: StepOutputContract['contract']) => {
  switch (contract.artifact_evidence_profile) {
    case 'workbook':
      return 'Workbook artifact';
    case 'blob_review':
      return 'File artifact';
    case 'external_resource':
      return 'External resource';
    default:
      return contract.artifact_kind === 'immutable_file' ? 'File artifact' : null;
  }
};

const DataInputSchemaContracts = ({
  outputContracts,
  bottomSpacing,
  showSourceLabel = false,
}: Props) => {
  const [selectedContract, setSelectedContract] = useState<StepOutputContract | null>(null);

  const visibleContracts = useMemo(
    () =>
      outputContracts.filter(
        ({ contract }) =>
          contract.kind !== OutputContractKind.None ||
          contract.availability === OutputAvailability.RuntimeOptional ||
          contract.original_file_transport === 'runtime_checked',
      ),
    [outputContracts],
  );

  if (visibleContracts.length === 0) {
    return null;
  }

  const modalContract = selectedContract?.contract;
  const modalArtifactLabel = modalContract ? getArtifactLabel(modalContract) : null;
  const schemaJson = modalContract?.json_schema
    ? JSON.stringify(modalContract.json_schema, null, 2)
    : null;

  return (
    <>
      <Group gap="6px" mt="6px" mb={bottomSpacing} wrap="wrap">
        {visibleContracts.map((outputContract) => {
          const { contract } = outputContract;
          const schemaLabel = getSchemaLabel(outputContract);
          const sourceLabel = getSourceLabel(outputContract);
          const artifactLabel = getArtifactLabel(contract);
          const color = getContractColor(contract.kind);
          const Icon =
            contract.kind === OutputContractKind.Incompatible ? IconAlertTriangle : IconBraces;
          const baseButtonLabel = artifactLabel
            ? showSourceLabel
              ? `${sourceLabel}: ${artifactLabel}`
              : `Input artifact: ${artifactLabel}`
            : showSourceLabel
              ? `${sourceLabel}: ${schemaLabel}`
              : `Input schema: ${schemaLabel}`;
          const buttonLabel =
            contract.availability === OutputAvailability.RuntimeOptional
              ? `${baseButtonLabel} · May be unavailable`
              : baseButtonLabel;

          return (
            <Button
              key={`${outputContract.data_output.step_id}-${outputContract.data_output.output_id ?? 'default'}`}
              variant="light"
              color={color}
              size="compact-xs"
              radius="sm"
              leftSection={<Icon size={13} />}
              onClick={() => setSelectedContract(outputContract)}
              styles={{
                label: {
                  fontSize: '11px',
                  lineHeight: 1.2,
                  whiteSpace: 'normal',
                },
              }}
            >
              {buttonLabel}
            </Button>
          );
        })}
      </Group>

      <StyledModal
        opened={selectedContract !== null}
        onClose={() => setSelectedContract(null)}
        title={modalArtifactLabel ? 'Input Artifact' : 'Input Schema'}
        scrollType="body"
        size="lg"
      >
        {selectedContract && modalContract && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge color={getContractColor(modalContract.kind)} variant="light" size="sm">
                {getContractKindLabel(modalContract.kind)}
              </Badge>
              <Badge color="gray" variant="light" size="sm">
                {modalContract.dataset_type}
              </Badge>
              {modalArtifactLabel && (
                <Badge color="blue" variant="light" size="sm">
                  {modalArtifactLabel}
                </Badge>
              )}
              <Badge
                color={
                  modalContract.availability === OutputAvailability.RuntimeOptional ? 'yellow' : 'gray'
                }
                variant="light"
                size="sm"
              >
                {getAvailabilityLabel(modalContract.availability)}
              </Badge>
            </Group>

            {modalContract.availability === OutputAvailability.RuntimeOptional && (
              <Text size="13px" c="yellow.9">
                This output may not be produced on every run.
              </Text>
            )}

            {modalContract.original_file_transport === 'runtime_checked' && (
              <Text size="13px">
                Can supply an original file to a child File input. The run checks that processing
                finished and the original file is available.
              </Text>
            )}

            <Stack gap={2}>
              <Text size="11px" c="dimmed" fw={500}>
                Source
              </Text>
              <Text size="13px">{getSourceLabel(selectedContract)}</Text>
            </Stack>

            <Stack gap={2}>
              <Text size="11px" c="dimmed" fw={500}>
                {modalArtifactLabel ? 'Contract' : 'Schema'}
              </Text>
              <Text size="13px">{getSchemaLabel(selectedContract)}</Text>
            </Stack>

            {modalArtifactLabel && (
              <Stack gap={2}>
                <Text size="11px" c="dimmed" fw={500}>
                  Artifact contract
                </Text>
                <Text size="13px">
                  {modalArtifactLabel} · {modalContract.dataset_type}
                </Text>
              </Stack>
            )}

            {(modalContract.expected_mime_types?.length ?? 0) > 0 && (
              <Stack gap={2}>
                <Text size="11px" c="dimmed" fw={500}>
                  Expected MIME types
                </Text>
                {modalContract.expected_mime_types?.map((mimeType) => (
                  <Text key={mimeType} size="13px">
                    {mimeType}
                  </Text>
                ))}
              </Stack>
            )}

            {modalContract.description && (
              <Stack gap={2}>
                <Text size="11px" c="dimmed" fw={500}>
                  Description
                </Text>
                <Text size="13px">{modalContract.description}</Text>
              </Stack>
            )}

            {modalContract.diagnostics.length > 0 && (
              <Stack gap={4}>
                <Text size="11px" c="dimmed" fw={500}>
                  Diagnostics
                </Text>
                <Stack gap={4}>
                  {modalContract.diagnostics.map((diagnostic, index) => (
                    <Text key={`${diagnostic}-${index}`} size="12px" c="red">
                      {diagnostic}
                    </Text>
                  ))}
                </Stack>
              </Stack>
            )}

            {schemaJson && (
              <Stack gap={4}>
                <Text size="11px" c="dimmed" fw={500}>
                  JSON Schema
                </Text>
                <Box
                  component="pre"
                  p="xs"
                  style={{
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: '6px',
                    color: '#0F172A',
                    fontSize: '11px',
                    lineHeight: 1.45,
                    margin: 0,
                    overflowX: 'auto',
                    whiteSpace: 'pre',
                  }}
                >
                  {schemaJson}
                </Box>
              </Stack>
            )}
          </Stack>
        )}
      </StyledModal>
    </>
  );
};

export default DataInputSchemaContracts;
